"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { CircleCheck as CheckCircle2, Calendar, Search, ArrowRight, ShieldCheck, UserCheck, Sparkles, Clock, Loader } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
const benefitsList = [{
  title: "100% Direct Expert Access",
  desc: "You work directly with me, Yassine Essaadi. No account managers or junior staff."
}, {
  title: "Tailored Growth Strategy",
  desc: "Custom SEO roadmap designed around your specific business objectives and industry."
}, {
  title: "Transparent & Honest Reporting",
  desc: "Clear deliverables, practical implementation, and realistic ROI metrics without agency fluff."
}];
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-card border-border rounded-xl p-8 border shadow-sm text-center">
        <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-accent/10 text-accent">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="font-serif text-2xl font-bold text-foreground mb-2">
          Consultation Requested!
        </h3>
        <p className="text-muted-foreground mb-6">
          {successMessage}
        </p>
        <Button type="button" onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-muted">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Please enter your name",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Your Full Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Sarah Jenkins" {...field} className="bg-background border-input px-4 py-2.5 text-foreground focus-visible:ring-ring" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Please enter your email address",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Work Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="sarah@yourcompany.com" {...field} className="bg-background border-input px-4 py-2.5 text-foreground focus-visible:ring-ring" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Please enter your website URL"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Website URL</FormLabel>
            <FormControl>
              <Input placeholder="https://yourcompany.com" {...field} className="bg-background border-input px-4 py-2.5 text-foreground focus-visible:ring-ring" />
            </FormControl>
            <FormDescription className="text-muted-foreground text-xs">
              I will run an initial review of your domain prior to our call.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="bg-destructive/10 border-destructive/20 border p-3 rounded-md">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 text-base font-semibold rounded-md transition-colors shadow-md flex items-center justify-center gap-2">
          {isSubmitting ? <>
              <Loader className="animate-spin size-5" />
              <span>Preparing Request...</span>
            </> : <>
              <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
              <span>Book a Free SEO Consultation</span>
            </>}
        </Button>
      </div>

      <p className="text-center text-xs text-muted-foreground pt-1 flex items-center justify-center gap-1.5">
        <Clock className="size-3.5 text-muted-foreground" />
        <span>Response within 24 business hours. No hard sell.</span>
      </p>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultationcta" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 size-96 rounded-full bg-secondary/30 blur-3xl opacity-50" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-primary/5 blur-3xl opacity-50" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-4">
              <Badge variant="secondary" className="bg-secondary text-secondary-foreground border-border px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full inline-flex items-center gap-1.5">
                <Sparkles className="size-3.5 text-accent" />
                <span>Direct SEO Consultancy</span>
              </Badge>

              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground leading-tight tracking-tight">
                Ready to Grow Your Search Visibility with a Dedicated SEO Expert?
              </h2>

              <p className="text-lg text-muted-foreground leading-relaxed">
                When you partner with YE Consultancy, you work directly with me — Yassine Essaadi. 
                I deliver hands-on strategy and execution designed to increase search performance, qualified leads, and sustainable revenue for UK and international businesses.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              {benefitsList.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border/60 shadow-xs hover:border-border transition-colors">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                    <UserCheck className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-serif text-base font-semibold text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4 border-t border-border/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="size-5 text-accent" />
                <span className="text-sm text-muted-foreground">
                  Need a custom scope or technical audit first?
                </span>
              </div>
              
              <Button asChild variant="outline" className="border-primary/20 text-primary hover:bg-primary/5 hover:text-primary rounded-md text-sm font-semibold h-10">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Get a Free Local SEO Audit</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <Card className="border-border bg-card shadow-xl rounded-2xl overflow-hidden relative">
              <div className="bg-primary px-6 py-5 text-primary-foreground border-b border-primary/20">
                <div className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-primary-foreground/80 mb-1">
                  <Search className="size-4" />
                  <span>Free Initial Discovery</span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-primary-foreground">
                  Book Your SEO Session
                </h3>
                <p className="text-primary-foreground/80 text-xs sm:text-sm mt-1">
                  Fill in your details to reserve a 30-minute 1-on-1 strategy call with Yassine.
                </p>
              </div>

              <CardContent className="p-6 sm:p-8">
                <FormProvider formId="consultation-cta-form" sectionName="consultationcta" defaultValues={{
                name: "",
                email: "",
                website: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                website: {
                  required: true
                }
              }} submitText="Book a Free SEO Consultation" successMessage="Thank you for reaching out! I will review your website and reply with convenient times for our call.">
                  <ConsultationFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </section>;
}